import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { uploadToCloudinary } from '../lib/cloudinary'
import { useAuth } from '../contexts/AuthContext'
import './ProfileSettings.css'

export default function ProfileSettings() {
  const { user, profile, fetchProfile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const avatarInputRef = useRef()
  const bannerInputRef = useRef()
  const debounceRef = useRef()

  const [form, setForm] = useState({
    display_name: '',
    username: '',
    bio: '',
    website: '',
  })
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)
  const [bannerFile, setBannerFile] = useState(null)
  const [usernameStatus, setUsernameStatus] = useState(null) // null | 'checking' | 'available' | 'taken' | 'same'
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!authLoading && !user) { navigate('/login'); return }
    if (profile) {
      setForm({
        display_name: profile.display_name || profile.full_name || '',
        username: profile.username || '',
        bio: profile.bio || '',
        website: profile.website || '',
      })
      setAvatarPreview(profile.avatar_url || null)
      setBannerPreview(profile.banner_url || null)
    }
  }, [profile, authLoading])

  // Username availability check
  useEffect(() => {
    if (!form.username || form.username === profile?.username) { setUsernameStatus('same'); return }
    if (!/^[a-z0-9_]{3,20}$/.test(form.username)) { setUsernameStatus(null); return }
    setUsernameStatus('checking')
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const { data } = await supabase.from('profiles').select('id').eq('username', form.username).neq('id', user.id).single()
      setUsernameStatus(data ? 'taken' : 'available')
    }, 500)
  }, [form.username])

  function handleAvatarChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  function handleBannerChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setBannerFile(file)
    setBannerPreview(URL.createObjectURL(file))
  }

  async function uploadImage(file, folder) {
    return await uploadToCloudinary(file, folder)
  }

  async function handleSave(e) {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validate username
    if (!form.username) { setError('Username is required.'); return }
    if (!/^[a-z0-9_]{3,20}$/.test(form.username)) { setError('Username must be 3–20 chars, lowercase letters, numbers, underscores only.'); return }
    if (usernameStatus === 'taken') { setError('That username is already taken.'); return }

    setSaving(true)
    try {
      let avatarUrl = profile?.avatar_url || null
      let bannerUrl = profile?.banner_url || null

      if (avatarFile) {
        avatarUrl = await uploadImage(avatarFile, 'avatars')
      }
      if (bannerFile) {
        bannerUrl = await uploadImage(bannerFile, 'banners')
      }

      const { error: err } = await supabase.from('profiles').update({
        display_name: form.display_name,
        username: form.username.toLowerCase(),
        bio: form.bio,
        website: form.website,
        avatar_url: avatarUrl,
        banner_url: bannerUrl,
        username_set: true,
      }).eq('id', user.id)

      if (err) throw err
      await fetchProfile(user.id)
      setSuccess('Profile saved!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  const displayName = form.display_name || profile?.full_name || 'Creator'

  if (authLoading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>

  return (
    <div className="settings-page page-fade">
      <div className="container">
        <div className="settings-header">
          <h1>Profile settings</h1>
          <p>Manage how you appear on HumanOrigin.</p>
        </div>

        <form onSubmit={handleSave}>
          <div className="settings-layout">
            <div className="settings-main">

              {/* Banner */}
              <div className="card settings-section">
                <h3>Profile banner</h3>
                <p className="hint" style={{ marginBottom: 16 }}>Shown at the top of your public profile. Recommended: 1200×400px.</p>
                <div className="banner-upload-area" onClick={() => bannerInputRef.current.click()}>
                  {bannerPreview ? (
                    <img src={bannerPreview} alt="Banner preview" className="banner-preview-img" />
                  ) : (
                    <div className="banner-placeholder">
                      <span>Click to upload banner</span>
                      <span className="hint">JPG, PNG, WebP — max 5MB</span>
                    </div>
                  )}
                  <div className="banner-overlay">Change banner</div>
                </div>
                <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBannerChange} />
                {bannerPreview && (
                  <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => { setBannerPreview(null); setBannerFile(null) }}>
                    Remove banner
                  </button>
                )}
              </div>

              {/* Avatar + Name */}
              <div className="card settings-section">
                <h3>Profile picture</h3>
                <p className="hint" style={{ marginBottom: 16 }}>Shown on your profile and beside your name. Recommended: square image, at least 200×200px.</p>
                <div className="avatar-upload-row">
                  <div className="avatar-upload-area" onClick={() => avatarInputRef.current.click()}>
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="avatar-preview-img" />
                    ) : (
                      <div className="avatar-placeholder">
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="avatar-overlay">✏️</div>
                  </div>
                  <div className="avatar-upload-info">
                    <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 8 }}>Click the image to upload a new photo.</p>
                    {avatarPreview && profile?.avatar_url && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAvatarPreview(null); setAvatarFile(null) }}>Remove photo</button>
                    )}
                  </div>
                </div>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
              </div>

              {/* Username */}
              <div className="card settings-section">
                <h3>Username</h3>
                <p className="hint" style={{ marginBottom: 16 }}>Your unique URL on HumanOrigin. Changing it will update your profile link.</p>
                <div className="form-group">
                  <div className="username-input-wrap">
                    <span className="username-at">@</span>
                    <input
                      type="text"
                      value={form.username}
                      onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                      placeholder="yourname"
                      maxLength={20}
                    />
                  </div>
                  {form.username && form.username !== profile?.username && (
                    <div className={`username-status ${usernameStatus}`}>
                      {usernameStatus === 'checking' && 'Checking...'}
                      {usernameStatus === 'available' && `✓ @${form.username} is available`}
                      {usernameStatus === 'taken' && `✕ @${form.username} is already taken`}
                    </div>
                  )}
                  <span className="hint">humanorigin.app/u/<strong>{form.username || 'yourname'}</strong></span>
                </div>
              </div>

              {/* Display name + Bio + Website */}
              <div className="card settings-section">
                <h3>About you</h3>
                <div className="form-group">
                  <label>Display name</label>
                  <input type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Your Name" maxLength={60} />
                  <span className="hint">This is shown instead of your username on your profile.</span>
                </div>
                <div className="form-group">
                  <label>Bio</label>
                  <textarea
                    rows={4}
                    value={form.bio}
                    onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                    placeholder="Illustrator based in Tokyo. I draw things with pencils and occasionally spill coffee on them."
                    maxLength={300}
                  />
                  <span className="hint">{form.bio.length}/300 characters</span>
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://yoursite.com" />
                </div>
              </div>

            </div>

            {/* Sidebar preview */}
            <div className="settings-sidebar">
              <div className="card">
                <h4 style={{ marginBottom: 16 }}>Preview</h4>
                <div className="profile-preview">
                  <div className="preview-banner">
                    {bannerPreview
                      ? <img src={bannerPreview} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div className="preview-banner-empty" />
                    }
                  </div>
                  <div className="preview-body">
                    <div className="preview-avatar">
                      {avatarPreview
                        ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        : <span>{displayName.charAt(0).toUpperCase()}</span>
                      }
                    </div>
                    <div className="preview-name">{displayName}</div>
                    <div className="preview-username">@{form.username || 'yourname'}</div>
                    {form.bio && <div className="preview-bio">{form.bio.slice(0, 100)}{form.bio.length > 100 ? '…' : ''}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="error-msg" style={{ marginTop: 16, marginBottom: 8 }}>{error}</div>}
          {success && <div className="success-msg" style={{ marginTop: 16, marginBottom: 8 }}>{success}</div>}

          <div className="settings-actions">
            <button type="submit" className="btn btn-primary" disabled={saving || usernameStatus === 'taken' || usernameStatus === 'checking'}>
              {saving ? <><span className="loading-spinner" /> Saving...</> : 'Save profile'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}
